<template>
  <div v-if="currentUser && customizations" class="invoices-tab">
    <Transition>
      <div v-if="currentInvoice && invoiceBelongsToCurrentCustomer">
        <InvoiceHeader
          :invoice="currentInvoice"
          @refresh-invoice="refreshInvoice"
        >
          <template #title>
            <h3>
              <Breadcrumbs
                :crumbs="crumbs"
                root-text="Invoices"
                @click="handleCrumbClick"
              />
            </h3>
          </template>
        </InvoiceHeader>

        <Invoice
          v-if="currentInvoice"
          class="ssui-invoice"
          :invoice="currentInvoice"
          :customizations="customizations"
          :equipment-types-enabled="equipmentTypesEnabled"
          :tenant-timezone="tenantTimezone"
          @edit-transaction-note-click="handleEditNoteClick"
          @edit-transaction-reference-id-click="handleEditReferenceIdClick"
          @equipment-request-click="toggleModal('captureModal')"
        >
          <template #paymentSchedule>
            <div
              v-if="spbEnabled && spbPlanStale && !spbPlanLocked"
              class="spb-stale-banner"
              data-cy="spb-stale-banner"
            >
              <i class="fas fa-exclamation-triangle mr-2" />
              <div>
                <strong>{{ programName }} is out of date.</strong>
                The dollar-based {{ programName }} total no longer matches the
                {{ programName }} target. Reconfigure the
                {{ programName }} before accepting any quote on this job —
                approval will be blocked until the {{ programName }} is updated.
              </div>
            </div>
            <div
              v-if="spbEnabled && spbPlanNeedsReview && !spbPlanLocked"
              class="spb-review-banner"
              data-cy="spb-review-banner"
            >
              <i class="fas fa-info-circle mr-2" />
              <div>
                <strong>{{ programName }} needs review.</strong>
                The invoice total has changed since this {{ programName }} was
                created. Open Edit {{ programName }}, confirm the
                {{ intervalName.toLowerCase() }} percentages against the updated
                balance, and save. Invoice approval will be blocked until the
                {{ programName }} is reviewed.
              </div>
            </div>
            <PaymentScheduleSection
              v-if="jobPaymentSchedule"
              :schedule="jobPaymentSchedule"
              :payment-context="currentInvoice?.progress_billing ?? null"
            />
          </template>
        </Invoice>
        <EditTransactionNotesModal
          v-if="editNotesModal"
          :transaction="selectedTransaction"
          @hide-modal="clearNotesModal"
        />
        <TransactionReferenceIdModal
          v-if="editReferenceIdModal"
          :transaction="selectedTransaction"
          @hide-modal="clearReferenceIdModal"
        />
        <EquipmentCaptureModal
          v-if="captureModal"
          :invoice="currentInvoice"
          :address="currentInvoice.service_address"
          :equipment="customerEquipment"
          @hide-modal="toggleModal('captureModal')"
          @refresh-invoice="refreshInvoice"
        />
      </div>
      <div v-else-if="!loading">
        <CustomerInvoicesListPane
          ref="invoicesTable"
          :filters="filters"
          flat
          :source="source"
          :customer-id="currentUser.id"
          :address="address"
          :show-sub-customers="currentUser.has_subs"
          @row-click="handleClick"
          @view-invoice="viewInvoice"
          @payment-success="handleMultiPaymentSuccess"
        />
      </div>
    </Transition>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import Transition from "@/components/Transition.vue";
import EquipmentCaptureModal from "@/Equipment/EquipmentCaptureModal.vue";
import InvoiceHeader from "@/Invoice/InvoiceHeader.vue";
import EditTransactionNotesModal from "@/Job/EditTransactionNotesModal.vue";
import TransactionReferenceIdModal from "@/Job/TransactionReferenceIdModal.vue";
import PaymentScheduleSection from "@/serviceshift-ui/components/QuoteInvoice/PaymentScheduleSection.vue";
import CustomerInvoicesListPane from "./CustomerInvoicesListPane.vue";

export default {
  components: {
    CustomerInvoicesListPane,
    Transition,
    EditTransactionNotesModal,
    TransactionReferenceIdModal,
    InvoiceHeader,
    EquipmentCaptureModal,
    PaymentScheduleSection
  },
  props: {
    address: {
      type: Object,
      default: null
    },
    filters: {
      type: Array,
      default: () => []
    },
    source: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      loading: false,
      crumbs: [],
      editNotesModal: false,
      editReferenceIdModal: false,
      selectedTransaction: null,
      captureModal: false,
      jobPaymentSchedule: null,
      jobPaymentScheduleLoading: false,
      progressBillingConfig: null
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant"]),
    programName() {
      return (
        this.$store.state.tenant?.feature_flags
          ?.progress_billing_program_name ||
        this.currentInvoice?.progress_billing?.program_name ||
        "Payment Schedule"
      );
    },
    intervalName() {
      return (
        this.$store.state.tenant?.feature_flags
          ?.progress_billing_interval_name ||
        this.currentInvoice?.progress_billing?.interval_name ||
        "Installment"
      );
    },
    ...mapState({
      currentUser: "currentUser",
      currentInvoice: "currentInvoice",
      customerEquipment: "customerEquipment",
      customizations: ({ tenantCustomizations, tenant }) => {
        if (!tenant || !tenantCustomizations) {
          return null;
        }
        return {
          ...tenantCustomizations,
          Tenant: { ...tenant }
        };
      },
      equipmentTypesEnabled({ tenant }) {
        if (!tenant) return false;
        return tenant.feature_flags.equipment_types;
      },
      tenantTimezone({ tenant }) {
        if (!tenant) return "";
        return tenant.timezone;
      }
    }),
    invoiceBelongsToCurrentCustomer() {
      if (!this.currentInvoice || !this.currentUser) return false;
      return this.currentInvoice.customer?.customer_id === this.currentUser.id;
    },
    spbJobId() {
      return (
        this.currentInvoice?.job?.id ??
        this.currentInvoice?.job?.job_id ??
        this.currentInvoice?.job_id ??
        null
      );
    },
    progressBillingEnabled() {
      return (
        this.featureEnabledForTenant("progress_billing") &&
        Boolean(this.progressBillingConfig?.enabled)
      );
    },
    existingProgressBillingInvoice() {
      return (
        this.currentInvoice?.progress_billing?.is_progress_billing === true
      );
    },
    shouldLoadProgressBillingSchedule() {
      return (
        this.featureEnabledForTenant("progress_billing") &&
        (this.progressBillingEnabled || this.existingProgressBillingInvoice)
      );
    },
    spbEnabled() {
      return Boolean(this.jobPaymentSchedule);
    },
    spbPlanLocked() {
      return Boolean(this.currentInvoice?.finalized);
    },
    // Line-item drift: the invoice contract total shifted since the
    // plan was created. Applies to both split modes.
    spbPlanDrift() {
      return Boolean(this.currentInvoice?.progress_billing?.plan_drift);
    },
    spbPlanNeedsReview() {
      return Boolean(this.currentInvoice?.progress_billing?.plan_needs_review);
    },
    // Allocation drift: amount-mode plan whose installments no longer
    // sum to the snapshotted target.
    spbAllocationDrift() {
      const plan = this.jobPaymentSchedule;
      if (!plan || plan.split_mode !== "amount") return false;
      const planTotal = (plan.installments || []).reduce(
        (sum, i) => sum + Number(i.value || 0),
        0
      );
      const target = plan.target_total_cents ?? 0;
      return target > 0 && planTotal !== target;
    },
    spbPlanStale() {
      return this.spbPlanDrift || this.spbAllocationDrift;
    }
  },
  watch: {
    currentInvoice(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.handleInvoiceChange();
        this.loadJobPaymentSchedule();
      }
    },
    currentUser(newUser) {
      if (
        newUser &&
        this.currentInvoice &&
        this.currentInvoice.customer?.customer_id !== newUser.id
      ) {
        window.Sentry?.withScope((scope) => {
          scope.setLevel("warning");
          scope.setTag("ticket", "TOF-521");
          scope.setExtras({
            url: window.location.href,
            newCustomerId: newUser.id,
            newCustomerName: newUser.full_name,
            staleInvoiceId: this.currentInvoice.invoice_id,
            staleInvoiceCustomerId: this.currentInvoice.customer?.customer_id,
            staleInvoiceBillingCustomerId:
              this.currentInvoice.billing_customer?.customer_id
          });
          window.Sentry.captureMessage(
            "Stale invoice on customer change — clearing"
          );
        });
        this.setCurrentInvoice(null);
      }
    }
  },
  async created() {
    window.addEventListener("popstate", this.navigateBack);
    EventBus.$on("refresh-invoice", this.refreshInvoice);
    EventBus.$on("navigate-to-invoice", this.viewInvoice);

    const params = getQueryVariables();
    if (params.invoice_id) {
      await Promise.all([
        this.loadInvoice(params.invoice_id),
        this.fetchTenantCustomizations()
      ]);
    }
    if (!this.tenant && !this.fetchingTenant) {
      await this.fetchTenant();
    }
    await this.loadProgressBillingConfig();
    await this.loadJobPaymentSchedule();
  },
  beforeUnmount() {
    window.removeEventListener("popstate", this.navigateBack);
    this.setCurrentInvoice(null);
    EventBus.$off("refresh-invoice", this.refreshInvoice);
    EventBus.$off("navigate-to-invoice", this.viewInvoice);
  },
  methods: {
    ...mapActions([
      "fetchInvoice",
      "fetchJob",
      "fetchTenant",
      "setCurrentInvoice",
      "fetchTenantCustomizations",
      "fetchJobPaymentSchedule",
      "fetchProgressBillingConfig"
    ]),
    getCrumbTitle() {
      return `Invoice #${this.currentInvoice.invoice_id}`;
    },
    handleClick(row) {
      this.loadInvoice(row.invoice_id);
    },
    viewInvoice(id) {
      this.loadInvoice(id);
    },
    async loadInvoice(id) {
      this.loading = true;
      try {
        await this.fetchInvoice(id);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error fetching invoice", error);
      } finally {
        this.loading = false;
      }
    },
    handleCrumbClick(crumb) {
      if (crumb.id === "root") {
        this.setCurrentInvoice(null);
      }
    },
    handleInvoiceChange() {
      if (this.currentInvoice) {
        const { cancelled, voided, kind, invoice_id } = this.currentInvoice;
        const title = this.getCrumbTitle();
        const className = "";
        this.crumbs = [
          {
            id: invoice_id,
            label: title,
            className
          }
        ];
        updateQueryParam("invoice_id", invoice_id);
      } else {
        updateQueryParam("invoice_id", null);
      }
    },
    handleEditNoteClick(transaction) {
      this.selectedTransaction = transaction;
      this.editNotesModal = true;
    },
    handleEditReferenceIdClick(transaction) {
      this.selectedTransaction = transaction;
      this.editReferenceIdModal = true;
    },
    clearNotesModal() {
      this.editNotesModal = false;
      this.selectedTransaction = null;
    },
    clearReferenceIdModal() {
      this.editReferenceIdModal = false;
      this.selectedTransaction = null;
    },
    toggleModal(modal) {
      this[modal] = !this[modal];
    },
    refreshInvoiceList() {
      this.$refs.invoicesTable?.refresh();
    },
    handleMultiPaymentSuccess() {
      // The pane refreshes its own table; a paid invoice can't be open in
      // the detail branch (the list renders only while it's closed), so
      // refreshing the current invoice is unnecessary here.
      this.refreshInvoiceList();
    },
    refreshInvoice() {
      if (this.currentInvoice) {
        this.loadInvoice(this.currentInvoice.invoice_id);
      }
      // Local `jobPaymentSchedule` is the source of truth for the
      // Payment Schedule table and Edit modal — reload it any time the
      // invoice gets refreshed so a refund-with-cancel or a deposit
      // payment is reflected immediately, without forcing a page reload.
      if (this.shouldLoadProgressBillingSchedule && this.spbJobId) {
        this.loadJobPaymentSchedule();
      }
    },
    navigateBack() {
      const params = getQueryVariables();
      if (params.invoice_id == undefined) {
        this.setCurrentInvoice(null);
      } else {
        this.viewInvoice(params.invoice_id);
      }
    },

    async loadProgressBillingConfig() {
      if (!this.featureEnabledForTenant("progress_billing")) return;
      try {
        this.progressBillingConfig = await this.fetchProgressBillingConfig();
      } catch {
        this.progressBillingConfig = null;
      }
    },

    async loadJobPaymentSchedule() {
      if (!this.shouldLoadProgressBillingSchedule) {
        this.jobPaymentSchedule = null;
        return;
      }
      const jobId = this.spbJobId;
      if (!jobId) {
        this.jobPaymentSchedule = null;
        return;
      }
      this.jobPaymentScheduleLoading = true;
      try {
        const response = await this.fetchJobPaymentSchedule(jobId);
        this.jobPaymentSchedule = response?.schedule ?? null;
      } catch {
        this.jobPaymentSchedule = null;
      } finally {
        this.jobPaymentScheduleLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.invoices-tab:empty {
  min-height: 100vh;
}
:deep(.crumb-title-alert .crumb) {
  background: var(--danger-color);
  border-radius: 3px;
  color: #fff;
  padding: 0 3px;
}

// Fixes SSUI invoice header table bottom left/right
// border radius overridden by general.scss stylesheet
.invoices-tab {
  :deep(.header-content .table) {
    margin-bottom: 0;
    border-radius: 5px 5px 0 0;
  }
}

:deep(.invoice-body) {
  .table {
    margin-bottom: 0;
  }
}

:deep(.table td) {
  border-top: none; // Removes bootstrap default border display
  vertical-align: baseline !important; // Removes bootstrap default column align
}

.spb-stale-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0.5rem 0 0.75rem;
  padding: 0.75rem 1rem;
  border-left: 4px solid #f59e0b;
  background-color: #fffbeb;
  color: #78350f;
  font-size: 0.9rem;
  line-height: 1.4;

  i {
    margin-top: 2px;
    color: #b45309;
  }

  strong {
    display: block;
    margin-bottom: 0.15rem;
  }
}

.spb-review-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 1.5rem 0 0.75rem;
  padding: 0.75rem 1rem;
  border-left: 4px solid #3b82f6;
  background-color: #eff6ff;
  color: #1e3a8a;
  font-size: 0.9rem;
  line-height: 1.4;

  i {
    margin-top: 2px;
    color: #1d4ed8;
  }

  strong {
    display: block;
    margin-bottom: 0.15rem;
  }
}
</style>
